import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { CardSpacer } from "@dashboard/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { Savebar } from "@dashboard/components/Savebar";
import { WsmErrorFragment } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { Box, Checkbox, Text } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { getWsmDealerFormErrors } from "../../errors";
import { sectionMessages, settingsMessages } from "../../messages";
import { WsmDealerSettingsFormData } from "../../types";
import { wsmDealerGroupListPath } from "../../urls";
import WsmDealerHelpCard from "../WsmDealerHelpCard/WsmDealerHelpCard";
import WsmDealerTabs from "../WsmDealerTabs/WsmDealerTabs";

export interface WsmDealerSettingsPageProps {
  initialData: WsmDealerSettingsFormData;
  errors: WsmErrorFragment[];
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  onSubmit: (data: WsmDealerSettingsFormData) => void;
}

export const WsmDealerSettingsPage = ({
  initialData,
  errors,
  disabled,
  saveButtonBarState,
  onSubmit,
}: WsmDealerSettingsPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const [data, setData] = useState<WsmDealerSettingsFormData>(initialData);
  const formErrors = getWsmDealerFormErrors(errors, intl);

  return (
    <DetailPageLayout>
      <TopNav title={intl.formatMessage(sectionMessages.settingsTitle)} />
      <DetailPageLayout.Content>
        <WsmDealerTabs current="settings" />
        <CardSpacer />
        <DashboardCard data-test-id="wsm-dealer-settings">
          <DashboardCard.Header>
            <DashboardCard.Title>
              {intl.formatMessage(settingsMessages.cardTitle)}
            </DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            {formErrors.map(message => (
              <Text
                key={message}
                size={3}
                color="critical1"
                data-test-id="wsm-dealer-settings-error"
              >
                {message}
              </Text>
            ))}
            <Checkbox
              data-test-id="wsm-dealer-settings-stacking"
              disabled={disabled}
              name="discountStacking"
              checked={data.discountStacking}
              onCheckedChange={checked => setData({ discountStacking: !!checked })}
            >
              <Text size={3}>{intl.formatMessage(settingsMessages.discountStacking)}</Text>
            </Checkbox>
            <Box paddingLeft={5} paddingTop={1}>
              <Text size={2} color="default2">
                {intl.formatMessage(settingsMessages.discountStackingHelp)}
              </Text>
            </Box>
          </DashboardCard.Content>
        </DashboardCard>
      </DetailPageLayout.Content>

      <DetailPageLayout.RightSidebar>
        <WsmDealerHelpCard />
      </DetailPageLayout.RightSidebar>

      <Savebar>
        <Savebar.Spacer />
        <Savebar.CancelButton onClick={() => navigate(wsmDealerGroupListPath)} />
        <Savebar.ConfirmButton
          transitionState={saveButtonBarState}
          onClick={() => onSubmit(data)}
          disabled={disabled}
          data-test-id="wsm-dealer-settings-save"
        />
      </Savebar>
    </DetailPageLayout>
  );
};

export default WsmDealerSettingsPage;
